import { getEnvironment, UprightEnvironment } from '@upright/common/environment';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { FinalImpactScoreType } from '@upright/common/model/output/scores/types';
import { Valence } from '@upright/common/model/types';
import { friendlyLowerCaseScoreUnit, friendlyShortImpactTitle } from '@upright/common/utils';
import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { BouLink } from '@upright/ui/common/generic/content/texts/bou-link';
import { getTextStyle } from '@upright/ui/common/generic/content/texts/bou-text/text-style';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useOnClickOutside, useViewSetting } from '@upright/ui/common/generic/hooks';
import { mobileOrNarrower, tabletOrNarrower } from '@upright/ui/common/generic/layout/breakpoint-utils';
import { PlainModal } from '@upright/ui/common/generic/modals/plain-modal';
import { commonOverlaySmallShadow } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/styles';
import { useCarouselContext } from '@upright/ui/common/upright/core/impact-lens-profile/context';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import { FigureBoxes } from '@upright/ui/common/upright/core/impact-lenses/net-impact/info-boxes/single-impact/figure-boxes';
import { signForValenceAndValue } from '@upright/ui/common/upright/model/output/scores/net-impact-chart/shared';
import { formatImpactScore } from '@upright/ui/common/upright/model/output/scores/net-impact-chart/value-formatter';
import DrilldownIcon from '@upright/ui/icons/img/streamline-mini-line/interface-essential/text-formatting/interface-text-formatting-list-bullets.svg';
import { friendlyShortestImpactTitle } from '@upright/ui/untidy/texts';
import { useModelEntityDrilldownUrl } from '@upright/ui/untidy/urls';
import { DrillDownMode } from '@upright/ui/views/model-entity-views/company-view/drilldown/types';
import { isNil } from 'lodash';
import { useRef } from 'react';
import styled, { css } from 'styled-components';

export function ComplexImpactDetailsInfoModal() {
  const context = useNetImpactLensContext();
  const carouselContext = useCarouselContext();
  const ref = useRef(null);
  const pushGtmEvent = usePushGtmEvent();
  const getDrilldownUrl = useModelEntityDrilldownUrl();
  // Dev-only extras inside the modal. The render below additionally checks
  // `environment !== AWS_PRODUCTION`, so this toggle alone is not enough to
  // surface them in production.
  const expertMode = useViewSetting('netImpact.complexInfoModalDevExtras');
  const environment = getEnvironment();

  const handleClickOutside = () => {
    if (context?.options.disableStateClearingOnOutsideClick) {
      return;
    }
    const newState = carouselContext.lens.getClearedSelectionState?.(carouselContext.selectionState);
    if (newState) {
      carouselContext.setSelectionState(newState);
    }
  };
  useOnClickOutside([ref], () => {
    handleClickOutside();
  });

  if (!context) {
    return <></>;
  }

  const data = context.data;
  const selection = context.state;
  const valence = selection.valence;
  const impactCode = selection.impact;
  const scores = data?.scores;
  const formatters = context.options.formatters;
  const scoreReader = context.options.scoreReader;
  const entity = data?.entity;
  const resultId = data?.modelResultId;
  const scoreUnit = context.options.scoreUnit;

  const value = scoreReader.readScore(impactCode, undefined, undefined, valence);
  if (isNil(value) || !entity || !scores || !impactCode || !valence) {
    return <></>;
  }

  const name = entity.name;

  const _formattedValue = formatImpactScore(value, formatters, scoreUnit);

  const formattedValue =
    context.options.scoreUnit === FinalImpactScoreType.CENTS_PER_DOLLAR
      ? `${_formattedValue}  ${friendlyLowerCaseScoreUnit(FinalImpactScoreType.CENTS_PER_DOLLAR)}`
      : _formattedValue;

  const showDrilldownLink = resultId !== undefined && !context.options.disableLinks;

  const title = (
    <Title>
      <TitleTextPart>{getTitleText(name, impactCode, valence)}</TitleTextPart>
      <TitleFigureRoot>
        {signForValenceAndValue(valence, value)}
        {formattedValue}
      </TitleFigureRoot>
    </Title>
  );

  return (
    <Root ref={ref}>
      <PlainModal
        isOpen={true}
        closeModal={() => {
          handleClickOutside();
        }}
        accessibleName={'impact-details'}
        modalId="impact-details"
      >
        <Main data-testid={E2eTestElementId.IMPACT_DRILLDOWN}>
          {title}
          <FigureBoxes />
          {showDrilldownLink && (
            <Bottom>
              <LinkToDrillDown>
                <DrilldownIcon style={{ width: '1.0em', height: '1.0em', marginRight: '0.7em' }} />
                <BouLink
                  to={getDrilldownUrl({
                    id: entity.id,
                    type: entity.type,
                    impactCode,
                    valence,
                    name: entity.name,
                    resultId,
                  })}
                  onClick={() => {
                    pushGtmEvent({
                      event: CustomGtmEvent.IMPACT_PROFILE_NI_DRILLDOWN_CLICK,
                      data: { impactCode, valence },
                    });
                  }}
                >
                  View drilldown to contributing products
                </BouLink>
              </LinkToDrillDown>
              {expertMode && environment !== UprightEnvironment.AWS_PRODUCTION && (
                <LinkToDrillDown>
                  <DrilldownIcon style={{ width: '1.0em', height: '1.0em', marginRight: '0.7em' }} />
                  <BouLink
                    to={getDrilldownUrl({
                      id: entity.id,
                      type: entity.type,
                      impactCode,
                      valence,
                      name: entity.name,
                      resultId,
                      queryParams: {
                        mode: DrillDownMode.IMPACT_TOPICS,
                      },
                    })}
                  >
                    View impact topics drilldown
                  </BouLink>
                </LinkToDrillDown>
              )}
            </Bottom>
          )}
        </Main>
      </PlainModal>
    </Root>
  );
}

function companyNamePossessive(name: string | undefined) {
  if (!name || name.length > 15) {
    return `this company's`;
  }
  return <TitleDynamicElement>{name}'s</TitleDynamicElement>;
}

function getTitleText(name: string | undefined, impact: ImpactCategoryId, valence: Valence) {
  if (valence === Valence.NEGATIVE) {
    if (impact === ImpactCategoryId.GHG_EMISSIONS) {
      return (
        <>
          Economic cost of {companyNamePossessive(name)} <TitleDynamicElement>GHG emissions</TitleDynamicElement>
        </>
      );
    } else {
      return (
        <>
          Economic cost of {companyNamePossessive(name)}{' '}
          <TitleDynamicElement>{friendlyShortImpactTitle(impact)}</TitleDynamicElement> impact
        </>
      );
    }
  } else {
    return (
      <>
        Value of {companyNamePossessive(name)} positive
        <TitleDynamicElement>{friendlyShortestImpactTitle(impact)}</TitleDynamicElement> impact
      </>
    );
  }
}

const Root = styled.div`
  .ReactModal__Content {
    max-width: 95vw;
    /*
      The layout adjusts to a smaller layout at the 992px desktop breakpoint.
      Just before that, we need use all available screen width to fit everything in.
      The following kludge achieves that -- JO 18.4.2023
     */
    @media (max-width: 1050px) {
      @media (min-width: 991px) {
        max-width: 98vw;
      }
    }
  }
`;

const TitleDynamicElement = styled.div`
  display: inline;
  padding: 0.2em;
  border-radius: 8px;
  border: 1px solid ${BouColor.TOOL_BORDER_DARK_GRAY};
  margin-left: 0em;
  & + & {
    margin-left: 0.3em;
  }
`;

const Main = styled.div`
  background-color: white;
  border: 1px solid ${BouColor.TOOL_BORDER_CARDS_LIGHT_GRAY};
  border-radius: 4px;
  ${commonOverlaySmallShadow};
  text-align: left;
  font-size: 16px;
  ${tabletOrNarrower(css`
    max-width: 35em;
  `)};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-weight: 600;
  border-bottom: 1px solid ${BouColor.TOOL_BORDER_LIGHTER_GRAY};
  ${getTextStyle({ size: 'small' })}
  padding: 14px;
  padding-right: 4em;
  text-align: center;
  ${tabletOrNarrower(css`
    display: block;
    text-align: left;
  `)}
`;

const TitleTextPart = styled.div`
  flex-grow: 0;
`;

const TitleFigureRoot = styled.div`
  background-color: ${BouColor.YELLOW};
  margin-left: 0.5em;
  ${tabletOrNarrower(css`
    margin-left: 0;
    margin-top: 0.5em;
  `)}
  display: inline-flex;
`;

const Bottom = styled.div`
  border-top: 1px solid ${BouColor.TOOL_BORDER_LIGHTER_GRAY};
  ${getTextStyle({ size: 'xsmall' })}
  font-weight: bold;

  a {
    text-decoration: none;
  }

  padding: 14px;
  text-align: center;

  display: flex;
  flex-direction: row;
  gap: 2em;
  background-color: ${BouColor.TOOL_BACKGROUND_WHITE};
  z-index: 9998;
  ${mobileOrNarrower(css`
    border-top: 3px solid ${BouColor.TOOL_BORDER_LIGHTER_GRAY};
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1em;
  `)}
`;

const LinkToDrillDown = styled.div`
  display: flex;
  align-items: center;
`;
